import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useInfiniteQuery, useQuery } from "@tanstack/vue-query";
import axios from "axios";
import { CategoriesApi, CategoryType, FilterEventStatus } from "mws-ts-v2-sdk";

type CategoryListParams = {
  searchTerm?: string;
  type?: CategoryType;
  categoryIds?: string;
  pageSize?: number;
};

const getCategoriesApi = () => {
  return new CategoriesApi(
    undefined,
    useRuntimeConfig().public.catalogServiceUrl,
    axios,
  );
};

export const categoryQueryKeys = createQueryKeys("categories", {
  details: (categoryIdentifier: MaybeRefOrGetter<string | undefined>) => ({
    queryKey: [categoryIdentifier],
    queryFn: async () => {
      const response = await getCategoriesApi().v2CategoriesSlugGet({
        slug: toValue(categoryIdentifier) ?? "",
      });

      return response.data;
    },
  }),
  relatedCategories: (categorySlug: MaybeRefOrGetter<string>) => ({
    queryKey: [categorySlug],
    queryFn: async () => {
      const response = await getCategoriesApi().v2CategoriesSlugRelatedGet({
        slug: toValue(categorySlug),
      });

      return response.data;
    },
  }),
  list: (params: MaybeRef<CategoryListParams & { offset?: number }>) => ({
    queryKey: [params],
    queryFn: async () => {
      const unreffedParams = unref(params);
      const response = await getCategoriesApi().v2CategoriesGet(unreffedParams);

      return response.data;
    },
  }),
  liveEvents: (
    platformKey: MaybeRef<string>,
    filters: MaybeRef<{
      types: CategoryType[] | undefined;
      offset: number | undefined;
      pageSize: number | undefined;
    }>,
  ) => ({
    queryKey: [filters],
    queryFn: async () => {
      const unreffedFilters = unref(filters);

      const response = await getCategoriesApi().v2CategoriesLiveEventsGet({
        xPlatformKey: unref(platformKey),
        ...unreffedFilters,
      });

      return response.data;
    },
  }),
  filterCategories: (
    eventStatus: MaybeRefOrGetter<FilterEventStatus>,
    categorySlug: MaybeRefOrGetter<string>,
  ) => ({
    queryKey: ["events", eventStatus, categorySlug],
    queryFn: async () => {
      const response = await getCategoriesApi().v2CategoriesEventsGet({
        eventStatus: toValue(eventStatus),
        relatedCategorySlug:
          toValue(categorySlug).length > 0 ? toValue(categorySlug) : undefined,
      });

      return response.data;
    },
  }),
});

export const useInfiniteCategoriesListQuery = (
  params: Ref<CategoryListParams>,
) => {
  return useInfiniteQuery({
    queryKey: ["categories", "infiniteList", params], // we don't use `query-key-factory` because it doesn't support typing for infinite queries yet
    queryFn: async ({ pageParam }) => {
      const unreffedParams = unref(params);
      const response = await getCategoriesApi().v2CategoriesGet({
        ...unreffedParams,
        offset: pageParam,
      });

      return response.data;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const nextPageOffset = lastPage.offset + (params.value.pageSize || 25);
      if (nextPageOffset >= lastPage.totalCount) return undefined; // no next page

      return nextPageOffset;
    },
  });
};

export const useLiveCategoriesQuery = (params?: {
  enabled?: MaybeRef<boolean>;
}) => {
  const platformStore = usePlatformStore();
  const platformId = computed(
    () => platformStore.getPlatformConfiguration.platformId,
  );

  const isQueryEnabled = computed(() => {
    if (params?.enabled === undefined)
      return platformStore.getPlatformConfiguration.isAuctionsEnabled;

    return (
      unref(params.enabled) &&
      platformStore.getPlatformConfiguration.isAuctionsEnabled
    );
  });

  return useQuery({
    ...categoryQueryKeys.liveEvents(platformId, {
      types: [CategoryType.Team, CategoryType.Other],
      offset: 0,
      pageSize: 250,
    }),
    enabled: isQueryEnabled,
    staleTime: Infinity, // what live events are available doesn't change often
  });
};
